import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    HStack,
    Input,
    PinInput,
    PinInputField,
    Text,
    useToast,
    VStack
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { validatorsForm } from 'components/utils/validatorsForm'
import { AuthRequestConfirmDTO } from 'models'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import {
    getStatus,
    postStatus,
    resendSignupConfirmationCode
} from 'services/users'
import * as yup from 'yup'

const Confirmation: React.FC = () => {
    const navigate = useNavigate()
    const { id = '' } = useParams()

    const toast = useToast()

    const [confirmationCode, setConfirmationCode] = useState('')
    const [isCodeExpired, setIsCodeExpired] = useState(false)
    const [isResending, setIsResending] = useState(false)
    const [resendCooldown, setResendCooldown] = useState(0)

    const schema = yup
        .object({
            codigoConfirmacao: yup
                .string()
                .required(validatorsForm.codigoConfirmacao.required)
        })
        .required()

    const {
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm<AuthRequestConfirmDTO>({
        resolver: yupResolver(schema)
    })

    const onSubmit = async ({
        codigoConfirmacao
    }: AuthRequestConfirmDTO) => {
        return postStatus({
            id,
            codigoConfirmacao
        })
            .then(() => {
                navigate(`/cadastro/${id}`)
            })
            .catch(() => {
                toast({
                    title: 'Código inválido ou expirado!',
                    description:
                        'Verifique o código informado ou solicite um novo código.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            })
    }

    const handleResendCode = async () => {
        if (!id || isResending || resendCooldown > 0) {
            return
        }

        setIsResending(true)

        try {
            await resendSignupConfirmationCode(id)

            setConfirmationCode('')
            reset({
                codigoConfirmacao: ''
            })

            setIsCodeExpired(false)
            setResendCooldown(60)

            toast({
                title: 'Novo código enviado!',
                description:
                    'Enviamos um novo código de segurança para o seu e-mail.',
                position: 'bottom-left',
                status: 'success',
                duration: 5000,
                isClosable: true
            })
        } catch (error: any) {
            const status = error?.response?.status

            toast({
                title:
                    status === 429
                        ? 'Aguarde para solicitar novamente!'
                        : 'Não foi possível reenviar o código!',
                description:
                    status === 429
                        ? 'O limite de solicitações foi atingido. Tente novamente em alguns minutos.'
                        : 'Tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        } finally {
            setIsResending(false)
        }
    }

    useEffect(() => {
        if (!id) {
            return
        }

        getStatus(id)
            .then(({ status }) => {
                if (status === 200) {
                    navigate(`/cadastro/${id}`)
                }
            })
            .catch((error: any) => {
                const status = error?.response?.status

                switch (status) {
                    case 412:
                    case 422:
                        setIsCodeExpired(true)

                        toast({
                            title: 'Token de confirmação expirado!',
                            description:
                                'Solicite um novo código para continuar.',
                            position: 'bottom-left',
                            status: 'error',
                            duration: 5000,
                            isClosable: true
                        })
                        return

                    default:
                        toast({
                            title: 'Sentimos muito!',
                            description:
                                'Ocorreu um erro, tente novamente mais tarde.',
                            position: 'bottom-left',
                            status: 'error',
                            isClosable: true
                        })

                        navigate('/entrar')
                        break
                }
            })

        reset()
    }, [])

    useEffect(() => {
        if (resendCooldown <= 0) {
            return
        }

        const timeout = window.setTimeout(() => {
            setResendCooldown(current =>
                Math.max(current - 1, 0)
            )
        }, 1000)

        return () => window.clearTimeout(timeout)
    }, [resendCooldown])

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            h="100vh"
            w="100%"
        >
            <Box
                display="grid"
                w="100%"
                maxW="xl"
                gap={8}
            >
                <Box>
                    <Logo maxW={187} />
                </Box>

                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading
                        size="md"
                        textAlign="center"
                        mb="2"
                    >
                        Bem vind@ ao sistema!
                    </Heading>

                    <Text textAlign="center">
                        Confirme o código de segurança enviado por e-mail.
                    </Text>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout
                            type="container"
                            full
                            px={6}
                            mt={8}
                        >
                            <Layout
                                type="row"
                                gutter={{ y: 3 }}
                            >
                                <Layout
                                    type="col"
                                    layout={{ base: 12 }}
                                >
                                    <FormControl
                                        isInvalid={
                                            !!errors.codigoConfirmacao
                                        }
                                        isRequired
                                    >
                                        <FormLabel
                                            htmlFor="codigoConfirmacao"
                                            mx={0}
                                            textAlign="center"
                                            requiredIndicator={<></>}
                                        >
                                            Código de segurança
                                        </FormLabel>

                                        <HStack
                                            justifyContent="center"
                                            spacing="16px"
                                        >
                                            <PinInput
                                                placeholder="_"
                                                value={confirmationCode}
                                                isDisabled={
                                                    isCodeExpired ||
                                                    isResending
                                                }
                                                onChange={value => {
                                                    setConfirmationCode(
                                                        value
                                                    )

                                                    setValue(
                                                        'codigoConfirmacao',
                                                        value,
                                                        {
                                                            shouldValidate: true
                                                        }
                                                    )
                                                }}
                                            >
                                                {Array.from({
                                                    length: 6
                                                }).map((_, index) => (
                                                    <PinInputField
                                                        key={index}
                                                        data-testid={`Pin_${index}`}
                                                    />
                                                ))}
                                            </PinInput>

                                            <Input
                                                hidden
                                                {...register(
                                                    'codigoConfirmacao'
                                                )}
                                                required={false}
                                            />
                                        </HStack>

                                        <FormErrorMessage justifyContent="center">
                                            {
                                                errors.codigoConfirmacao
                                                    ?.message
                                            }
                                        </FormErrorMessage>
                                    </FormControl>
                                </Layout>

                                <Layout
                                    type="col"
                                    layout={{ base: 12 }}
                                    textAlign="center"
                                >
                                    <VStack spacing={3}>
                                        <Button
                                            w="full"
                                            maxW="320px"
                                            colorScheme="primary"
                                            size="md"
                                            type="submit"
                                            isLoading={isSubmitting}
                                            isDisabled={
                                                isCodeExpired ||
                                                isResending
                                            }
                                            data-testid="btn-submit"
                                        >
                                            Confirmar código
                                        </Button>

                                        <Text fontSize="sm">
                                            Não recebeu o código ou ele
                                            expirou?
                                        </Text>

                                        <Button
                                            type="button"
                                            variant="link"
                                            colorScheme="primary"
                                            isLoading={isResending}
                                            isDisabled={
                                                isSubmitting ||
                                                resendCooldown > 0
                                            }
                                            onClick={handleResendCode}
                                            data-testid="btn-resend-code"
                                        >
                                            {resendCooldown > 0
                                                ? `Reenviar código em ${resendCooldown}s`
                                                : 'Reenviar código'}
                                        </Button>
                                    </VStack>
                                </Layout>
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}

export default Confirmation